import * as _ from 'lodash';
import {
  Backdrop,
  Container,
  Fab,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { InitializedTable } from './list-table.init';
import { useListTableStyles, useTooltipStyles } from './list-table.styles';
import { ColumnCell, ColumnGroupCell, Path, ReactComponent } from './list-table.types';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useCurrentBreakpoint } from './use-current-breakpoint.hook';
import React, { CSSProperties, Fragment, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { LoadingIndicator } from '../loading-indicator/loading-indicator.component';
import { ArrowUpward } from '@mui/icons-material';
import { KnownSortOrder } from '@groundfloor-platform/mgmt-service-g-sdk';
import { TableComponents, TableVirtuoso, VirtuosoHandle } from 'react-virtuoso';

export interface ListTableProps<T extends object, U extends Path<T>, Q extends object = T> {
  uniqueKey: U;
  onSort: (update: { sortBy: string; sortOrder: KnownSortOrder }) => void;
  sortData: { sortBy: string; sortOrder: KnownSortOrder };
  table: InitializedTable<T, Q>;
  rows: T[];
  isLoading?: boolean;
  isPreviousData?: boolean;
  isBackgroundLoading?: boolean;
  hasError: boolean;
  errorMessage?: string;
  scrollUp?: {
    scrollUpAtScrollY: number;
  };
  customMessage?: string;
  removeShadow?: boolean;
  endReached?: () => void;
}

interface ListTableLocationState {
  index: number;
}

/** Generic table component */
export function ListTable<T extends object, U extends Path<T>, Q extends object = T>({
  uniqueKey,
  onSort,
  sortData,
  table,
  rows,
  isLoading,
  isPreviousData,
  hasError,
  errorMessage,
  scrollUp,
  customMessage,
  removeShadow,
  /** Fetching of next page is can be triggered by the table itself */
  endReached = _.noop
}: ListTableProps<T, U, Q>) {
  const isTouchDevice = useMediaQuery('(hover: none)');
  const hasRows = rows ? rows.length > 0 : false;
  const classes = useListTableStyles({
    hasRows,
    isTouchDevice
  });
  const currentBreakpoint = useCurrentBreakpoint();
  const columns = table.getColumns(currentBreakpoint);

  const config = table.getConfig();
  const styles = table.getStyles();

  const location = useLocation<ListTableLocationState>();
  const history = useHistory();
  const virtuoso = useRef<VirtuosoHandle>(null);

  // Scroll to top button controls
  const [showScrollUp, setShowScrollUp] = useState(false);
  function onClickScrollUp() {
    window.scrollTo(0, 0);
  }
  useEffect(() => {
    function listen() {
      if (window.scrollY > 600) {
        setShowScrollUp(true);
      } else {
        setShowScrollUp(false);
      }
    }

    if (scrollUp !== undefined) {
      window.addEventListener('scroll', listen);

      return function () {
        window.removeEventListener('scroll', listen);
      };
    }

    return undefined;
  }, [scrollUp]);

  useEffect(() => {
    if (virtuoso.current && location?.state?.index) {
      virtuoso.current.scrollToIndex({
        index: location.state.index
      });
    }
  }, []);

  const tableRowItemContent = useMemo(() => GenerateTableRowItemContent(columns, table), [columns, table]);

  const virtuosoContext = useMemo<VirtuosoTableContext>(() => ({ tableStyle: styles.table }), [styles.table]);

  return (
    <Paper className={classes.tableWrapper} elevation={removeShadow ? 0 : 1}>
      <NoResultsMessage open={!hasRows && !isLoading && !hasError} customMessage={customMessage} />
      <ErrorMessage open={hasError} message={errorMessage} />
      <TableHeader show={table.includeTableHeader()} title={config.title} Controls={config.controls} />
      <LoadingOverlay isLoading={isLoading || isPreviousData} />

      <TableVirtuoso
        ref={virtuoso}
        rangeChanged={(range) =>
          history.replace(`${window.location.pathname}${window.location.search}`, { index: range.startIndex })
        }
        key={uniqueKey}
        useWindowScroll
        className={classes.table}
        components={VIRTUOSO_TABLE_COMPONENTS}
        context={virtuosoContext}
        data={rows}
        itemContent={tableRowItemContent}
        endReached={() => endReached()}
        overscan={200}
        fixedHeaderContent={() => (
          <TableRow style={{ backgroundColor: 'white' }}>
            {columns.map((column) => (
              <HeaderCell
                key={column.id}
                style={{ ...styles.th, ...column.options?.headerStyles }}
                isSortable={column.options?.isSortable === undefined ? false : column.options?.isSortable}
                isSorted={sortData.sortBy === column.options?.sortId}
                isSortedDesc={sortData.sortOrder === KnownSortOrder.Desc}
                tooltip={column.options?.tooltip}
                onClick={() =>
                  column.options?.isSortable && column.options?.sortId
                    ? onSort({
                        sortBy: column.options?.sortId,
                        sortOrder: sortData.sortOrder === KnownSortOrder.Desc ? KnownSortOrder.Asc : KnownSortOrder.Desc
                      })
                    : null
                }
              >
                {column.renderHeader()}
              </HeaderCell>
            ))}
          </TableRow>
        )}
      />

      {showScrollUp && (
        <Fab
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            backgroundColor: 'white'
          }}
          onClick={onClickScrollUp}
        >
          <ArrowUpward /> Top
        </Fab>
      )}
    </Paper>
  );
}

type VirtuosoTableContext = { tableStyle?: CSSProperties };

// Module-level so Virtuoso gets the same component types every render; inline definitions are
// new types each render, which remounts every row.
const VIRTUOSO_TABLE_COMPONENTS: TableComponents<any, VirtuosoTableContext> = {
  // `tableStyle` opts into `tableLayout: 'fixed'`. Under `auto` the browser sizes columns from
  // the rendered rows only, and Virtuoso renders a moving window, so they resize as you scroll.
  Table: ({ context, ...props }) => <Table {...props} style={{ borderCollapse: 'separate', ...context?.tableStyle }} />,
  // MUI v9 TableHead is an OverridableComponent, which Virtuoso's ComponentType slot rejects.
  TableHead: React.forwardRef(({ context, ...props }, ref) => <TableHead {...(props as any)} ref={ref} />),
  TableRow: ({ context, ...props }) => <TableRow {...props} />,
  TableBody: React.forwardRef(({ context, ...props }, ref) => <TableBody {...props} ref={ref} />)
};

function GenerateTableRowItemContent<T extends object, Q extends object = T>(
  columns: (ColumnCell<T, ReactComponent, Q> | ColumnGroupCell<T, ReactComponent, Q>)[],
  table: InitializedTable<T, Q>
) {
  return function CreateTableBodyRow(index: number, row: any) {
    return (
      <Fragment key={index}>
        {columns.map((column) => {
          if (column.type === 'COLUMN') {
            return (
              <TableCellWrapper row={row} table={table} key={column.id}>
                <span style={column.options?.styles}> {column.renderCell(row)}</span>
              </TableCellWrapper>
            );
          } else {
            return (
              <TableCellWrapper row={row} table={table} key={column.id}>
                {column.rows.map((colRow) => (
                  <span
                    key={colRow.id}
                    style={{
                      ...column.options?.styles,
                      display: 'flex',
                      flexDirection: 'column',
                      margin: '5px 0'
                    }}
                  >
                    {colRow.renderCell(row)}
                  </span>
                ))}
              </TableCellWrapper>
            );
          }
        })}
      </Fragment>
    );
  };
}

function TableHeader({ show, title, Controls }: { show: boolean; title?: string; Controls?: ReactComponent }) {
  const classes = useListTableStyles({});
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  if (!show) {
    return null;
  }
  return (
    <Grid
      container
      className={classes.tableHeader}
      spacing={isSmallScreen ? 1 : 0}
      sx={{
        alignItems: 'center'
      }}
    >
      <Grid
        size={{
          xs: 12,
          sm: 4
        }}
      >
        <Typography variant="h5" className={classes.tableTitle}>
          {title}
        </Typography>
      </Grid>
      <Grid
        container
        size={{
          xs: 12,
          sm: 8
        }}
        sx={{
          justifyContent: isSmallScreen ? 'flex-start' : 'flex-end'
        }}
      >
        {Controls && <Controls />}
      </Grid>
    </Grid>
  );
}

function TableCellWrapper<T extends object, Q extends object = T>({
  row,
  children,
  table
}: {
  row: T;
  children: ReactNode;
  table: InitializedTable<T, Q>;
}) {
  const classes = useListTableStyles({});
  const tableConfig = table.getConfig();
  const rowStyles = table.getRowStyles(row);

  const url = tableConfig.mapRowURL && tableConfig.mapRowURL(row);

  // Rendered inline rather than as a nested component: a component defined here is a new type
  // every render, which remounts the cell's contents.
  return (
    <TableCell className={`${classes.tableCell} ${classes.tableFont}`} style={rowStyles.td}>
      {url ? (
        <Link
          className={classes.tableCellContent}
          to={{
            pathname: url,
            state: tableConfig.tableLocationState
          }}
          style={rowStyles.a}
        >
          {children}
        </Link>
      ) : (
        <div className={classes.tableCellContent}>{children}</div>
      )}
    </TableCell>
  );
}

function HeaderCell({
  isSortable,
  isSorted,
  isSortedDesc,
  onClick,
  style,
  tooltip,
  children
}: {
  isSortable: boolean;
  isSorted: boolean;
  isSortedDesc: boolean;
  onClick: () => void | null;
  style: React.CSSProperties;
  tooltip?: string;
  children: ReactNode;
}) {
  const classes = useListTableStyles({});
  const tooltipClasses = useTooltipStyles();

  const [isOnMouseLeaveEffectLocked, setIsOnMouseLeaveEffectLocked] = useState<boolean>(false);
  const [displaySortDirection, setDisplaySortDirection] = useState<KnownSortOrder>(
    isSortedDesc ? KnownSortOrder.Desc : KnownSortOrder.Asc
  );

  function onMouseEnterOrLeave() {
    if (isOnMouseLeaveEffectLocked) {
      setIsOnMouseLeaveEffectLocked(false);
    } else {
      setDisplaySortDirection((state) => (state === KnownSortOrder.Asc ? KnownSortOrder.Desc : KnownSortOrder.Asc));
    }
  }

  function onClickSort() {
    setIsOnMouseLeaveEffectLocked(true);
    onClick();
  }

  const CellChildren = isSortable ? (
    <TableSortLabel
      onClick={onClickSort}
      active={isSorted}
      direction={displaySortDirection}
      style={{ color: '#979797' }}
      onMouseEnter={onMouseEnterOrLeave}
      onMouseLeave={onMouseEnterOrLeave}
    >
      {children}
    </TableSortLabel>
  ) : (
    <>{children}</>
  );

  return (
    <TableCell className={`${classes.tableHeaderFont} ${classes.header}`} style={style}>
      {tooltip ? (
        <Tooltip
          arrow
          classes={tooltipClasses}
          title={`Sort by ${tooltip} ${isSortedDesc ? 'ascending' : 'descending'}`}
        >
          {CellChildren}
        </Tooltip>
      ) : (
        CellChildren
      )}
    </TableCell>
  );
}

function PlaceholderMessage({ open, message }: { open: boolean; message: string }) {
  const classes = useListTableStyles({});
  return open ? <Container className={classes.placeholderMessage}>{message}</Container> : null;
}

export function NoResultsMessage({ open, customMessage }: { open: boolean; customMessage?: string }) {
  return <PlaceholderMessage open={open} message={customMessage ? customMessage : 'No data to display'} />;
}

export function ErrorMessage({ open, message }: { open: boolean; message?: string }) {
  return <PlaceholderMessage open={open} message={message || 'Sorry, something went wrong'} />;
}

export function LoadingOverlay({ isLoading }: { isLoading?: boolean }) {
  const classes = useListTableStyles({});
  return (
    <Backdrop open={isLoading || false} className={classes.backdrop}>
      <Container className={classes.loadingIndicatorContainer}>
        <LoadingIndicator />
      </Container>
    </Backdrop>
  );
}
